/**
 * Azure AD (Microsoft Entra) Authentication Configuration
 * Matches the pattern used in release tools
 */
import { Configuration, PopupRequest } from '@azure/msal-browser';

// Read configuration from public/config.json (populated during deployment)
export interface AppConfig {
  API_GATEWAY_URL: string;
  MSAL_CLIENT_ID: string;
  MSAL_SCOPE: string;
  MSAL_TENANT_ID?: string;
}

// Default configuration (override with config.json)
let appConfig: AppConfig = {
  API_GATEWAY_URL: import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3001/v1',
  MSAL_CLIENT_ID: import.meta.env.VITE_MSAL_CLIENT_ID || '',
  MSAL_SCOPE: import.meta.env.VITE_MSAL_SCOPE || '',
  MSAL_TENANT_ID: import.meta.env.VITE_MSAL_TENANT_ID || 'common',
};

// Load config from public/config.json
export const loadConfig = async (): Promise<AppConfig> => {
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const config = await response.json();
      appConfig = { ...appConfig, ...config };
    }
  } catch (error) {
    console.warn('Failed to load config.json, using environment variables', error);
  }
  return appConfig;
};

export const getConfig = (): AppConfig => appConfig;

/**
 * MSAL Configuration for Azure AD
 * See: https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/lib/msal-browser
 */
export const getMsalConfig = (): Configuration => ({
  auth: {
    clientId: appConfig.MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${appConfig.MSAL_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for persistence across tabs
    storeAuthStateInCookie: false, // Set to true for IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            console.info(message);
            return;
          case 3: // LogLevel.Verbose
            console.debug(message);
            return;
        }
      },
    },
  },
});

/**
 * Scopes to request during login
 * Uses getter to always fetch current config value (even after config.json loads)
 */
export const loginRequest: PopupRequest = {
  get scopes() {
    return [appConfig.MSAL_SCOPE];
  },
};

/**
 * Scopes for silent token acquisition
 * Uses getter to always fetch current config value (even after config.json loads)
 */
export const tokenRequest = {
  get scopes() {
    return [appConfig.MSAL_SCOPE];
  },
};
